import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { FileDown, Loader2, Printer } from 'lucide-react';
import api from '../../../utils/api';
import {
  assertBlobKind,
  downloadBlob,
  filenameFromDisposition,
  parseBlobError,
} from '../../../utils/blobDownload';
import PdfEmbed from '../../PdfEmbed';
import { isNativePlatform } from '../../../utils/device';

function defaultFilename(title) {
  const safe = String(title || 'report-card').replace(/[^a-zA-Z0-9_-]+/g, '-');
  return `${safe}.pdf`;
}

export default function PortalReportCardPreviewModal({
  open,
  onOpenChange,
  params,
  title = 'Report card',
}) {
  const [loading, setLoading] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [filename, setFilename] = useState('');
  const [printUrl, setPrintUrl] = useState('');
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const canFetch = Boolean(open && params);
  const paramsKey = useMemo(() => (params ? JSON.stringify(params) : ''), [params]);

  useEffect(() => {
    if (!canFetch) {
      setPdfBlob(null);
      setFilename('');
      setPrintUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      setError('');
      return undefined;
    }

    let cancelled = false;

    setLoading(true);
    setError('');
    setPdfBlob(null);
    setFilename('');
    setPrintUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });

    api.get('/portal/student/report-card/pdf', { params, responseType: 'blob' })
      .then(async (res) => {
        if (cancelled) return;
        const blob = res.data;
        const apiMessage = await parseBlobError(blob);
        if (apiMessage) {
          throw new Error(apiMessage);
        }
        await assertBlobKind(blob, 'pdf');
        if (cancelled) return;
        setPdfBlob(blob);
        setFilename(filenameFromDisposition(
          res.headers['content-disposition'],
          defaultFilename(title),
        ));
        setPrintUrl(URL.createObjectURL(blob));
      })
      .catch(async (e) => {
        if (cancelled) return;
        let message = e?.response?.data?.message || e?.message;
        if (!message && e?.response?.data instanceof Blob) {
          message = await parseBlobError(e.response.data);
        }
        setError(message || 'Failed to load preview');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canFetch, paramsKey, params, title]);

  useEffect(() => () => {
    setPrintUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  }, []);

  const handleDownload = async () => {
    if (!pdfBlob || downloading) return;
    setDownloading(true);
    try {
      await downloadBlob(pdfBlob, filename || defaultFilename(title));
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    if (isNativePlatform()) return;
    if (!printUrl) return;
    const w = window.open(printUrl, '_blank');
    if (!w) return;
    w.addEventListener('load', () => {
      w.focus();
      w.print();
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] flex flex-col">
        <DialogHeader className="flex-row items-center justify-between space-y-0 gap-2">
          <DialogTitle>Report card preview — {title}</DialogTitle>
          <div className="flex shrink-0 gap-2">
            <Button type="button" variant="outline" size="sm" disabled={!pdfBlob || loading || !!error || downloading} onClick={handleDownload}>
              {downloading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-2" />
              )}
              Download
            </Button>
            {!isNativePlatform() && (
              <Button type="button" variant="outline" size="sm" disabled={!printUrl} onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 rounded-lg border overflow-auto bg-white">
          {loading ? (
            <div className="h-full min-h-[200px] flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Generating PDF preview…
            </div>
          ) : error ? (
            <div className="h-full min-h-[200px] flex items-center justify-center text-red-500 px-4 text-center">
              {error}
            </div>
          ) : (
            <PdfEmbed
              title="report-card-preview"
              blob={pdfBlob}
              className="w-full min-h-full bg-white border-0"
              style={{ minHeight: '70vh' }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
